import { useEffect, useState } from 'react';
import { defaultOptionIndex } from '../api/pollyPricing.js';
import { formatCurrency } from '../../../utils/format.js';
import { equityFirstLabel, isActivator, productName } from '../utils/loanTypes.js';
import { AccordionItem } from './AccordionItem.js';
import { getOrgConfig } from '../../../utils/organizationConfigUtil.js';
import { IoAlertCircleOutline, IoCaretBackCircleOutline, IoCaretDownCircleOutline, IoCaretForwardCircleOutline, IoCaretUpCircleOutline } from "react-icons/io5";
import IconButton from './IconButton.js';
import { SkyCloudLoader } from '../../loaders/SkyCloudLoader.jsx';

function numberToWords(num) {
    if (num === 0) return "Zero";

    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const scales = ["", "Thousand", "Million", "Billion", "Trillion"];

    // Helper function to convert numbers under 1000
    function convertLessThanThousand(n) {
        if (n === 0) return "";

        if (n < 20) {
            return ones[n] + " ";
        } else if (n < 100) {
            return tens[Math.floor(n / 10)] + " " + convertLessThanThousand(n % 10);
        } else {
            return ones[Math.floor(n / 100)] + " Hundred " + convertLessThanThousand(n % 100);
        }
    }

    let result = "";
    let scaleIndex = 0;

    // Loop through the number chunks of 3 digits from right to left
    while (num > 0) {
        let chunk = num % 1000;
        if (chunk !== 0) {
            let chunkWords = convertLessThanThousand(chunk);
            result = chunkWords + scales[scaleIndex] + " " + result;
        }
        num = Math.floor(num / 1000);
        scaleIndex++;
    }

    return result.trim();
}

export function ResultsPanelPolly({ result, isPricing, form, scenario }) {
    return (
        <div className="content_response">
            {isPricing ? <>
                <div className="response_loading">
                    {getOrgConfig().id !== 'SkyTPO' ? <img src="content/Images/loading.gif" alt="Loading..." /> : <SkyCloudLoader />}
                </div>
            </> :
                <Body result={result} isPricing={isPricing} form={form} scenario={scenario} />
            }
        </div>
    );
}

function Body({ result, isPricing, form, scenario }) {
    if (!result) return null;

    switch (result.status) {
        case 'priced':
            return <PricedResult result={result} form={form} scenario={scenario} isPricing={isPricing} />;
        case 'ineligible':
            return <IneligibleResult programs={result.programs} correspondent={scenario.correspondent} />;
        case 'unavailable':
            return <div className="response_result"><div className="error_msg">Rates not available</div></div>;
        default:
            return (
                <>
                    <div className="response_title">{result.title}</div>
                    <div className="response_result">
                        <div className="error_msg">{friendlyMessage(result.message)}</div>
                    </div>
                </>
            );
    }
}

function friendlyMessage(message = '') {
    // The engine's broker-facing copy is shown as-is; anything else is an internal
    // string the original also keeps hidden.
    if (message.includes('If you still need assistance, contact your AE')) return message;
    return 'Sorry, an error occurred. Please refresh the page to try again.';
}

function displayValue({ form, option, type = "", correspondent = false }) {
    switch (type.toLowerCase()) {
        case "p&i":
            return formatCurrency(option.principalAndInterest);
        case "i/o":
            const interestOnlyAmount = (form.loanAmount * (option.rate / 100)) / 12;
            return formatCurrency(interestOnlyAmount);
        case "points":
            return (correspondent ? option.price : option.discount).toFixed(3);
        default:
            return option.rate.toFixed(3);
    }
}

function PricedResult({ result, form, scenario, isPricing }) {
    const { programs } = result;
    const [programIndex, setProgramIndex] = useState(0);
    const [optionIndex, setOptionIndex] = useState(0);
    const [showAllRates, setShowAllRates] = useState(false);

    const { correspondent, typeCode } = scenario;

    useEffect(() => {
        setProgramIndex(0);
        setOptionIndex(defaultOptionIndex(programs[0]?.prices, correspondent));
        setShowAllRates(false);
    }, [programs, correspondent]);

    const program = programs[Math.min(programIndex, programs.length - 1)];
    const items = program?.prices ?? [];
    const option = items[Math.min(optionIndex, items.length - 1)];
    if (!option) return null;

    const POLLY_ACTIVATOR_PRIME_NAME = 'Activator Prime';
    const multiple = programs.length > 1;
    const title = `${numberToWords(programs.length) ?? programs.length} loan program${multiple ? 's' : ''} found`;
    const isInterestOnly = form.interestOnly;
    const isCDFI = form.cdfiEligible;

    const selectProgram = (next) => {
        setProgramIndex(next);
        setOptionIndex(defaultOptionIndex(programs[next].prices, correspondent));
        setShowAllRates(false);
    };

    const loanTypeName = productName(typeCode, {
        occupancy: form.occupancy,
        incomeType: form.incomeType,
        pricedProgram: program.name,
        loanProductName: scenario.productName,
    });

    // Hidden for an org with no comparison sheet of its own, rather than
    // showing brokers another company's artwork.
    const activatorPrimeComparisonImage = getOrgConfig().quickPriceActivatorPrimeComparisonImage;

    const ficoLimit = result.limits?.FicoMin;
    const ltvLimit = result.limits?.LTVMax;

    return (
        <>
            <div className="flex justify-between">
                <div className="response_title">{title}</div>
                {multiple && isActivator(typeCode) && programs.filter(x => x.name?.includes(POLLY_ACTIVATOR_PRIME_NAME)) > 0 && activatorPrimeComparisonImage && <div div className="response_compare">
                    <button className="btn btn-default btn-sm" style={{ color: "rgb(255, 15, 99)" }} onClick={() => window.open(activatorPrimeComparisonImage, '_blank', 'noopener,noreferrer')}><span className="glyphicon glyphicon-record"></span> Compare</button>
                </div>}
            </div >
            <div className="response_result" aria-busy={isPricing}>
                <div className={`headline_loantype${multiple ? ' has_switcher' : ''}`}>{loanTypeName}</div>

                <div className="flex program_name">
                    {multiple && (
                        <IconButton
                            icon={IoCaretBackCircleOutline}
                            disabled={programIndex === 0}
                            onClick={() => selectProgram(programIndex - 1)}
                            aria-label="Previous program"
                            className="rounded-full text-2xl left_button"
                        />
                    )}
                    <span>{program.name}</span>
                    {multiple && (
                        <IconButton
                            icon={IoCaretForwardCircleOutline}
                            disabled={programIndex >= programs.length - 1}
                            onClick={() => selectProgram(programIndex + 1)}
                            aria-label="Next program"
                            className="rounded-full text-2xl right_button"
                        />
                    )}
                </div>

                <div className="program_headline">
                    <div className="headline_fico_ltv">
                        <span>FICO: {form.noFico ? 'N/A' : form.fico}</span>
                        <IoAlertCircleOutline size={21} className='alert-icon' title={form.noFico ? 'No FICO' : `Min FICO: ${ficoLimit ?? 300}`} />
                        <span className="gap" />
                        <span>LTV: {form.ltv}</span>
                        <IoAlertCircleOutline size={21} className='alert-icon' title={`Max LTV: ${ltvLimit ?? 90}`} />
                    </div>
                    <div className="headline_all_rates">
                        <a role="button" tabIndex={0} onClick={() => setShowAllRates((v) => !v)}
                            onKeyDown={(e) => e.key === 'Enter' && setShowAllRates((v) => !v)}>
                            {showAllRates ? 'Hide All Rates' : 'Show All Rates'}
                        </a>
                    </div>
                </div>

                <div className="program_option">
                    <Tile
                        className="option_col_1"
                        name="Rate"
                        value={displayValue({ form: form, option: option, correspondent: correspondent })}
                        stepper={items.length > 1}
                        canDown={optionIndex < items.length - 1}
                        canUp={optionIndex > 0}
                        onDown={() => setOptionIndex((i) => Math.min(items.length - 1, i + 1))}
                        onUp={() => setOptionIndex((i) => Math.max(0, i - 1))}
                    />
                    <Tile
                        className="option_col_2"
                        name={`Monthly Payment (${isInterestOnly ? 'I/O' : 'P&I'})`}
                        value={isInterestOnly ? displayValue({ form: form, option: option, type: 'i/o', correspondent: correspondent }) : displayValue({ form: form, option: option, type: 'p&i', correspondent: correspondent })}
                        stepper={items.length > 1}
                        canDown={optionIndex < items.length - 1}
                        canUp={optionIndex > 0}
                        onDown={() => setOptionIndex((i) => Math.min(items.length - 1, i + 1))}
                        onUp={() => setOptionIndex((i) => Math.max(0, i - 1))}
                    />
                    <Tile
                        className="option_col_3"
                        // Correspondent quotes a price; Wholesale quotes points either way.
                        name={
                            correspondent
                                ? 'Price'
                                : isCDFI ? 'Lender points' : 'Discount points'
                        }
                        value={displayValue({ form: form, option: option, type: 'points', correspondent: correspondent })}
                        stepper={items.length > 1}
                        canDown={optionIndex < items.length - 1}
                        canUp={optionIndex > 0}
                        onDown={() => setOptionIndex((i) => Math.min(items.length - 1, i + 1))}
                        onUp={() => setOptionIndex((i) => Math.max(0, i - 1))}
                    />
                </div>

                {showAllRates && (
                    <AllRates form={form} program={program} items={items} isInterestOnly={isInterestOnly} correspondent={correspondent} />
                )}

                {equityFirstHint(result.secondIneligibles)}

                <div className="program_note">
                    <ul>
                        <li>Pricing is provided based on the data entered.</li>
                        {form.propertyType === '2' && (
                            <li>This is assuming an Attached Condo. If it&apos;s a Detached Condo, select SFR.</li>
                        )}
                        <li>
                            If criteria change once the loan is submitted based on the criteria of the subject
                            loan file, pricing will be updated accordingly.
                        </li>
                        {Number(form.dti) > 50 && <li>$3,000 residual income required for DTI over 50%.</li>}
                        {form.state === 'NJ' && form.occupancy === '2' && form.pppTerm !== '0' && (
                            <li>Closing in the name of an Entity is required for Prepay Penalties in NJ.</li>
                        )}
                        <li>
                            Pricing as of {result.pricingTimeStamp} Pacific.{' '}
                            {result.rateExpired && <span className="program_expired">Expired</span>}
                        </li>
                        {programs.some((p) => p.name.includes('BPACCEQFST')) && (
                            <li>Please review the product matrix for credit event seasoning requirements.</li>
                        )}
                        {form.state === 'IL' && form.closingEntity === '3' && Number(form.units) < 5 && (
                            <li>In IL, PPP is required if APR is &lt;8%, and PPP buyout is required if APR is &gt;=8%.</li>
                        )}
                    </ul>
                </div>
            </div>
        </>
    );
}

/** Equity First declines purely on prepay terms get a nudge instead of a rejection. */
function equityFirstHint(secondIneligibles = []) {
    const first = secondIneligibles[0];
    if (!first) return null;
    const onlyPrepay =
        first.Reasons.length === 1 && first.Reasons[0].endsWith(' PREPAY REQUIRED');
    if (!onlyPrepay) return null;
    return (
        <div className="equity_first_note">
            Our {equityFirstLabel()} program may be eligible if you select 5 Year 5% fixed Prepay.
        </div>
    );
}

function Tile({ className, name, value, stepper, canUp, canDown, onUp, onDown }) {
    return (
        <div className={className}>
            <span className="option_name">{name}</span>
            {stepper ? (
                <div className="flex justify-between px-0.5 items-center">
                    <IconButton
                        icon={IoCaretDownCircleOutline}
                        disabled={!canDown}
                        onClick={onDown}
                        aria-label="Higher rate"
                        className="rounded-full text-2xl p-0 down_button"
                    />
                    <span className="option_value">{value}</span>
                    <IconButton
                        icon={IoCaretUpCircleOutline}
                        disabled={!canUp}
                        onClick={onUp}
                        aria-label="Lower rate"
                        className="rounded-full text-2xl p-0 up_button"
                    />
                </div>
            ) : <span className="option_value block text-center">{value}</span>}
        </div>
    );
}

function AllRates({ form, program, items, isInterestOnly, correspondent }) {
    const [expanded, setExpanded] = useState(null);
    const isArm = program.name.includes('ARM');

    return (
        <div className="program_all_rates">
            <div className="rate_boxer">
                <table className="tb_all_rates">
                    <thead>
                        <tr>
                            <th width="80">Rate</th>
                            <th width="130">Price/Discount</th>
                            <th width="160">Monthly Pmt ({isInterestOnly ? 'I/O' : 'P&I'})</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <RateRow
                                key={`${item.RateOptionId}-${index}`}
                                form={form}
                                program={program}
                                item={item}
                                isArm={isArm}
                                isInterestOnly={isInterestOnly}
                                isExpanded={expanded === index}
                                onToggle={() => setExpanded(expanded === index ? null : index)}
                                correspondent={correspondent}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RateRow({ form, program, item, isArm, isInterestOnly, isExpanded, onToggle, correspondent }) {
    // const shown = (list = []) => list.filter((adj) => !adj.HiddenOnPage);
    // console.log(item, correspondent);
    const adjustmentRules = program.ruleResults.filter(x => x.category.toLowerCase() === "Adjustment".toLowerCase() && x.booleanEquationValue && x.resultEquationValue !== 0) ?? [];
    // console.log('Adjustments: ', adjustmentRules);

    return (
        <>
            <tr>
                <td>
                    <button type="button" onClick={onToggle} aria-expanded={isExpanded}>
                        {displayValue({ form: form, option: item, correspondent: correspondent })}
                    </button>
                </td>
                <td>{displayValue({ form: form, option: item, type: "points", correspondent: correspondent })}</td>
                <td>{isInterestOnly ? displayValue({ form: form, option: item, type: 'i/o', correspondent: correspondent }) : displayValue({ form: form, option: item, type: 'p&i', correspondent: correspondent })}</td>
            </tr>
            {isExpanded && (
                <tr className="expand">
                    <td colSpan={3}>
                        <table className="tb_price_expend">
                            <tbody>
                                <tr>
                                    <td className="adj_desc">BASE RATE</td>
                                    <td className="adj_val">{item.rateBeforeAdjustments.toFixed(3)}</td>
                                </tr>
                                {adjustmentRules.filter(x => x.target.toLowerCase() === "Rate".toLowerCase())?.map((adj, i) => (
                                    <tr key={`r${i}`}>
                                        <td className="adj_desc">{adj.ruleName}</td>
                                        <td className="adj_val">{adj.resultEquationValue.toFixed(3)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <td className="adj_desc"><b>FINAL RATE</b></td>
                                    <td className="adj_val"><b>{displayValue({ form: form, option: item, correspondent: correspondent })}</b></td>
                                </tr>
                                <tr><td colSpan={2} className="adj_desc">&nbsp;</td></tr>
                                <tr>
                                    <td className="adj_desc">
                                        BASE PRICE/DISCOUNT
                                    </td>
                                    <td className="adj_val">{(correspondent ? item.priceAfterBaseAdjustments : (item.discount - Math.abs(item.totalPriceAdjustments))).toFixed(3)}</td>
                                </tr>
                                {adjustmentRules.filter(x => x.target.toLowerCase() === "Price".toLowerCase())?.map((adj, i) => (
                                    <tr key={`r${i}`}>
                                        <td className="adj_desc">{adj.ruleName}</td>
                                        <td className="adj_val">{(correspondent ? adj.resultEquationValue : (adj.resultEquationValue * -1)).toFixed(3)}</td>
                                    </tr>
                                ))}
                                {(correspondent && (item.unclampedPrice - item.price) > 0) && <tr>
                                    <td className="adj_desc">MAX PRICE</td>
                                    <td className="adj_val">{(Math.abs(item.unclampedPrice - item.price) * -1).toFixed(3)}</td>
                                </tr>}
                                <tr>
                                    <td className="adj_desc">
                                        <b>TOTAL PRICE/DISCOUNT</b>
                                    </td>
                                    <td className="adj_val"><b>{displayValue({ form: form, option: item, type: 'points', correspondent: correspondent })}</b></td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                </tr>
            )}
        </>
    );
}

function IneligibleResult({ programs, correspondent }) {
    // console.log('Ineligible: ', programs);
    const orgConfig = getOrgConfig();
    return (
        <>
            <div className="results-head">
                <div className="response_title">Guidelines are not met for this scenario.</div>
            </div>
            <div className="response_result">
                {showIneligibleMessage() && <IneligiblePrograms programs={programs} />}
                {/* {showIneligibleMessage() && <div className="error_msg">
                    {programs.map((program, i) => {
                        const inEligibleRules = program.ruleResults.filter(x => x.category.toLowerCase() === "Eligibility".toLowerCase() && x.booleanEquationValue) ?? [];
                        return (
                            <div key={program.name ?? i}>
                                {programs.length > 1 && <div>{program.name}</div>}
                                {inEligibleRules.length > 0 ? <>
                                    <ul>
                                        {inEligibleRules.map((reason, j) => (
                                            <li key={j}>
                                                {reason.ruleName}<br /><span className="eva_error">{reason.booleanEquation}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </> : <GeneralExceptionMessage />}
                            </div>
                        )
                    })}
                </div>} */}
                <div className="error_msg">{orgConfig.ineligibleMessage}</div>
                {/* Exception requests are a Wholesale-channel offer only. */}
                {!correspondent && orgConfig.showIneligibleExceptionNote && (
                    <div className="exception_note">
                        Your loan may qualify for an exception,{' '}
                        <a href={orgConfig.links?.exceptionRequest} target="_blank" rel="noreferrer">
                            click here
                        </a>
                        .
                    </div>
                )}
            </div>
        </>
    );
}

function IneligiblePrograms({ programs }) {
    const inEligibleLists = programs.map(x => {
        const inEligibleRules = x.ruleResults.filter(y => y.category.toLowerCase() === "Eligibility".toLowerCase() && y.booleanEquationValue);
        return {
            title: x.name,
            content: inEligibleRules.length > 0 ? <ul className="list-disc m-0">
                {inEligibleRules.map((reason, j) => (
                    <li key={j}>
                        {reason.ruleName}
                        <br /><span className="eva_error">{reason.booleanEquation}</span>
                    </li>
                ))}
            </ul> : <GeneralExceptionMessage />
        }
    }) ?? [];

    const isMulti = false;
    const [openIndex, setOpenIndex] = useState(null);
    const handleToggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const [openIds, setOpenIds] = useState([]);
    const toggleItem = (id) => {
        if (openIds.includes(id)) {
            setOpenIds(openIds.filter((item) => item !== id));
        } else {
            setOpenIds([...openIds, id]);
        }
    };

    return (
        <>
            <p className="mb-6">Ineligible product(s) that match your search criteria.</p>
            {inEligibleLists.map((item, index) => (
                <AccordionItem
                    key={index}
                    title={item.title}
                    content={item.content}
                    isOpen={isMulti ? openIds.includes(index) : openIndex === index}
                    onClick={() => isMulti ? toggleItem(index) : handleToggle(index)}
                    contentClassName={"px-9"}
                />
            ))}
        </>
    );
}

function GeneralExceptionMessage() {
    return (
        <ul>
            <li>LTV, FICO OR LOAN AMOUNT REQUIREMENT NOT MET</li>
        </ul>
    )
}

function showIneligibleMessage() {
    const params = new URLSearchParams(window.location.search);
    return params.get('showIneligible')?.toLowerCase() === 'true'.toLowerCase() ?? false;
}